import React, { useEffect, useRef } from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "./FontAwesome";
import useStyles from "./Button.styles";
import { isNullOrEmpty } from "../../utils";
import useQuestion from "../../hooks/useQuestion";
import { LookupTableNavigation } from "../controls/KeyboardNavigation/LookupTableNavigation";
import { useKeyboardNavigationForLookupTable } from "../../hooks/useKeyboardNavigationForLookupTable";

interface Props {
    className?: string;
    disabled?: boolean;
    active?: boolean;
    hidden?: boolean;
    icon?: IconProp;
    iconPosition?: "start" | "end";
    iconOnHover?: boolean;
    size?: "small" | "large" | "full" | "flex" | number;
    text?: string;
    displayText?: boolean;
    displayIcon?: boolean;
    onClick(): void;
    imageUrl?: string;
    buttonType?: string;
    buttonId?: string;
    questionId?: string;
    onKeyDownEventHandler?(
        event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement | HTMLButtonElement>,
    ): void;
    buttonElementRef?: React.MutableRefObject<any>;
}

interface IconProps {
    active: boolean;
    disabled?: boolean;
    iconOnHover: boolean;
    icon: IconProp;
    iconPosition?: "start" | "end";
    size?: "small" | "large" | "full" | "flex" | number;
    textAndIcon: any;
    imageUrl?: string;
}

function getSizeClassName(classes, size) {
    if (typeof size === "number") {
        return "";
    }
    switch (size) {
        case "small":
            return classes.buttonSmall;
        case "full":
            return classes.buttonFull;
        case "flex":
            return classes.buttonFlex;
        default:
            return classes.buttonLarge;
    }
}

function ButtonIcon(props: IconProps) {
    const {
        size,
        active,
        disabled,
        icon,
        iconPosition,
        textAndIcon,
        iconOnHover,
        imageUrl,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme, size, iconPosition, textAndIcon, iconOnHover });

    return (
        <div
            className={
                !imageUrl &&
                classnames(
                    classes.buttonIconContainer,
                    textAndIcon && iconOnHover && classes.buttonIconAnimated,
                    textAndIcon && active && iconOnHover && classes.buttonIconAnimatedHover,
                )
            }
        >
            {!imageUrl && (
                <FontAwesomeIcon
                    fixedWidth
                    className={classnames(
                        classes.buttonIcon,
                        disabled && classes.buttonIconDisabled,
                    )}
                    icon={icon}
                />
            )}
            {imageUrl && <img src={imageUrl} className={classes.pointer} />}
        </div>
    );
}

export default function Button(props: Props) {
    const {
        size,
        className,
        disabled,
        onClick,
        active,
        hidden,
        text,
        icon,
        iconPosition,
        iconOnHover,
        displayText,
        displayIcon,
        imageUrl,
        buttonType,
        buttonId,
        onKeyDownEventHandler,
        buttonElementRef,
        questionId,
        ...otherProps
    } = props;

    const renderText = displayText !== false && !isNullOrEmpty(text);
    const renderIcon = displayIcon !== false && icon != null;
    const textAndIcon = renderText && renderIcon;

    const position = iconPosition || "end";

    const theme = useTheme();
    const classes = useStyles({ theme, iconPosition: position });

    const [_active, setActive] = React.useState(false);

    const isActive = active || _active;
    const buttonReference = useRef(null);

    const question = useQuestion();

    const { elementReference, onKeyDown } =
        (buttonType === "ADD_NEW_ITEM" || buttonType === "LOOKOUT_SEARCH_BTN") &&
        useKeyboardNavigationForLookupTable(
            question.id,
            buttonId,
            { id: question.id + buttonId },
            question,
            LookupTableNavigation,
            null,
            buttonType,
        );

    const refHandler = (el) => {
        buttonReference.current = el;
        if (buttonType === "ADD_NEW_ITEM" || buttonType === "LOOKOUT_SEARCH_BTN") {
            elementReference.current = el;
            if (elementReference) {
                elementReference.current = el;
            }
        }
        if (buttonElementRef) {
            buttonElementRef.current = el;
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", handleKeyPress);
        return function cleanup() {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, []);

    function handleKeyPress(event) {
        if (event.key !== "Enter") return;
        const focusedElement = document.activeElement;
        const excludedClasses = ["select__content", "select__option", "button"];
        const isExcludedElement = excludedClasses.some((className) =>
            focusedElement?.classList.contains(className),
        );

        // Exclude text input elements (INPUT and TEXTAREA) from Enter navigation
        const isTextInputElement =
            focusedElement &&
            (focusedElement.tagName === "INPUT" || focusedElement.tagName === "TEXTAREA");

        if (!isExcludedElement && !isTextInputElement) {
            const navigationButtons = {
                next: document.querySelector(".navigation__next"),
                submit: document.querySelector(".navigation__submit"),
            };

            const buttonToClick = navigationButtons.next || navigationButtons.submit;
            if (buttonToClick instanceof HTMLElement) {
                buttonToClick.click();
                event.preventDefault();
            }
        }
    }

    return (
        <button
            type="button"
            ref={(el) => refHandler(el)}
            aria-label={text}
            onFocus={() => setActive(true)}
            onBlur={() => setActive(false)}
            onMouseOver={() => setActive(true)}
            onMouseOut={() => setActive(false)}
            onClick={() => {
                if (disabled) {
                    return;
                }
                if (onClick) {
                    onClick();
                }
            }}
            onKeyPress={(event) => {
                if (disabled) {
                    return;
                }
                if (
                    buttonType !== "CLEARSTARRATING" &&
                    (event.key === " " || event.key === "Enter")
                ) {
                    if (onClick) {
                        onClick();
                    }
                    event.preventDefault();
                }
            }}
            onKeyDown={(event) => {
                if (
                    buttonType === "CLEARSTARRATING" &&
                    (event.key === "ArrowLeft" ||
                        event.key === "ArrowRight" ||
                        event.key === "ArrowUp" ||
                        event.key === "ArrowDown" ||
                        event.key === "Tab") &&
                    onKeyDownEventHandler
                ) {
                    onKeyDownEventHandler(event);
                    event.preventDefault();
                } else if (buttonType === "ADD_NEW_ITEM" || buttonType === "LOOKOUT_SEARCH_BTN") {
                    onKeyDown && onKeyDown(event);
                }
            }}
            className={classnames(
                className,
                !imageUrl && classes.button,
                !imageUrl && classes.buttonBorderNone,
                !imageUrl && getSizeClassName(classes, size),
                isActive && !disabled && !imageUrl && classes.buttonActive,
                disabled && classes.buttonDisabled,
                hidden && classes.buttonHidden,
            )}
            {...otherProps}
        >
            {position === "start" && renderIcon && (
                <ButtonIcon
                    size={size}
                    icon={icon}
                    iconPosition={position}
                    iconOnHover={iconOnHover}
                    active={isActive}
                    textAndIcon={textAndIcon}
                    disabled={disabled}
                    imageUrl={imageUrl}
                />
            )}
            {renderText && !imageUrl && (
                <div
                    className={classnames(
                        classes.buttonTextContainer,
                        textAndIcon && iconOnHover && classes.buttonTextAnimated,
                        textAndIcon && isActive && iconOnHover && classes.buttonTextAnimatedHover,
                    )}
                    aria-hidden="true"
                >
                    {text}
                </div>
            )}
            {position === "end" && renderIcon && (
                <ButtonIcon
                    size={size}
                    icon={icon}
                    active={isActive}
                    iconPosition={position}
                    textAndIcon={textAndIcon}
                    disabled={disabled}
                    iconOnHover={iconOnHover}
                    imageUrl={imageUrl}
                />
            )}
        </button>
    );
}
